import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getAllUsers } from "../../redux/slices/users.slice";
import { getAllScheduleFines } from "../../redux/slices/scheduleFine.slice";
import {
  clearReportsData,
  getReportData,
  onChangeReportsList,
} from "../../redux/slices/reports.slice";
import { reportTypes } from "../../utils/constants";
import { hasReportPermission } from "../../utils/rbac";
import { getAllSubAgents } from "../../redux/slices/agents.slice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  fineReportColumns,
  collectionColumns,
  agentCollectionLogColumns,
  agentCollectionSummaryColumns,
  statePerformanceColumns,
} from "../../utils/constants";
import { Download, TableView } from "@mui/icons-material";
import { downloadAgentCollectionExcel } from "../../utils/exportAgentCollectionExcel";

interface FineFilter {
  startDate: string | null;
  endDate: string | null;
  fineStatus?: string;
  createdBy?: string;
  agent?: string;
  offenceCode: string;
  pageNumber: number;
  perPage: number;
  sortingOn: string;
  isDescending: boolean;
  warden?: string;
}

type DateFieldErrors = {
  startDate?: string;
  endDate?: string;
};

const todayDateString = () => dayjs().format("YYYY-MM-DD");

const validateReportDates = (
  startDate: string,
  endDate: string,
  datesOptional = false,
): DateFieldErrors => {
  const errors: DateFieldErrors = {};
  const hasStart = Boolean(startDate?.trim());
  const hasEnd = Boolean(endDate?.trim());

  if (datesOptional) {
    if (!hasStart && !hasEnd) {
      return errors;
    }
    if (hasStart && !hasEnd) {
      errors.endDate = "End date is required when start date is set";
    }
    if (!hasStart && hasEnd) {
      errors.startDate = "Start date is required when end date is set";
    }
  } else {
    if (!hasStart) {
      errors.startDate = "Start date is required";
    }
    if (!hasEnd) {
      errors.endDate = "End date is required";
    }
  }

  const start = hasStart ? dayjs(startDate).startOf("day") : null;
  const end = hasEnd ? dayjs(endDate).startOf("day") : null;
  const today = dayjs().startOf("day");

  if (hasStart && start && !start.isValid()) {
    errors.startDate = "Enter a valid start date";
  }
  if (hasEnd && end && !end.isValid()) {
    errors.endDate = "Enter a valid end date";
  }

  if (start?.isValid() && end?.isValid()) {
    if (end.isBefore(start)) {
      errors.endDate = "End date cannot be before start date";
    }
  }

  if (start?.isValid() && start.isAfter(today)) {
    errors.startDate = "Start date cannot be in the future";
  }
  if (end?.isValid() && end.isAfter(today)) {
    errors.endDate = "End date cannot be in the future";
  }

  return errors;
};

const dateHelperTextSx = {
  minHeight: "1.25rem",
  marginLeft: 0,
  marginRight: 0,
};

const FinesReportForm: React.FC = () => {
  const [reportType, setReportType] = useState("Fines Report");
  const { data: user } = useSelector((state: RootState) => state.auth);

  const [dateErrors, setDateErrors] = useState<DateFieldErrors>({});

  const [formData, setFormData] = useState<FineFilter>({
    startDate: "",
    endDate: "",
    fineStatus: "all",
    createdBy: "all",
    agent: "all",
    offenceCode: "all",
    pageNumber: 1,
    perPage: 10,
    sortingOn: "Id",
    isDescending: true,
  });

  const dispatch = useDispatch<AppDispatch>();

  const { data: users, loading: usersLoading } = useSelector(
    (state: RootState) => state.users,
  );
  const { list: offences, loading: offencesLoading } = useSelector(
    (state: RootState) => state.scheduleFine,
  );
  const { subAgents, loading: agentsLoading } = useSelector(
    (state: RootState) => state.agents,
  );
  const { pageProps, data, loading } = useSelector(
    (state: RootState) => state.reports,
  );

  useEffect(() => {
    if (!users.length) {
      dispatch(getAllUsers());
    }
  }, [users.length, dispatch]);

  useEffect(() => {
    if (!subAgents.length && reportType === "Collection Report") {
      dispatch(getAllSubAgents());
    }
  }, [subAgents.length, dispatch, reportType]);

  useEffect(() => {
    if (!offences.length) {
      dispatch(getAllScheduleFines());
    }
  }, [offences.length, dispatch]);

  const datesOptional = reportType === "Agent Collection Report";

  const runDateValidation = (start: string, end: string) =>
    validateReportDates(start, end, datesOptional);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "startDate" || name === "endDate") {
        setDateErrors(
          runDateValidation(
            name === "startDate" ? value : prev.startDate || "",
            name === "endDate" ? value : prev.endDate || "",
          ),
        );
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const validation = runDateValidation(
      formData.startDate || "",
      formData.endDate || "",
    );
    if (validation.startDate || validation.endDate) {
      setDateErrors(validation);
      return;
    }
    setDateErrors({});

    const basePayload = {
      ...formData,
      startDate: formData.startDate
        ? dayjs(formData.startDate).startOf("day").toISOString()
        : null,
      endDate: formData.endDate
        ? dayjs(formData.endDate).endOf("day").toISOString()
        : null,
    };

    const finalPayload =
      reportType === "Collection Report"
        ? {
            startDate: basePayload.startDate,
            endDate: basePayload.endDate,
            agent: basePayload.agent,
            offenceCode: basePayload.offenceCode,
            pageNumber: 1,
            perPage: 10,
            sortingOn: "CreatedDate",
            isDescending: true,
          }
        : reportType === "Agent Collection Report"
          ? {
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              pageNumber: 1,
              perPage: 10,
              sortingOn: "",
              isDescending: true,
            }
          : basePayload;

    dispatch(
      onChangeReportsList({ ...pageProps, ...finalPayload, reportType }),
    );
    dispatch(getReportData({ reportType, ...finalPayload } as any));
  };

  const buildPdfRows = (rows: any[], columns: { id: string; label: string }[]) =>
    rows.map((row: any, index: number) => [
      index + 1,
      ...columns.map((col) => {
        const val = row[col.id];
        if (
          val === null ||
          val === undefined ||
          val === "" ||
          (typeof val === "string" && val.toLowerCase() === "null")
        ) {
          return "";
        }
        if (col.id.toLowerCase().includes("date")) {
          return dayjs(val).format("DD/MM/YYYY HH:mm");
        }
        if (typeof val === "number") {
          return val.toLocaleString();
        }
        return val.toString();
      }),
    ]);

  const generatePdf = (
    rows: any[],
    options?: { summaryRows?: any[]; detailTitle?: string },
  ) => {
    if (!rows?.length && !options?.summaryRows?.length) return;
    const doc = new jsPDF({ orientation: "landscape" });

    const companyName = "SLRSA";
    const title = reportType;
    const color = "#1691ff";
    const generatedAt = dayjs().format("YYYY-MM-DD HH:mm:ss");

    const pageWidth = doc.internal.pageSize.getWidth();

    // === Header ===
    doc.setTextColor(color);
    doc.setFontSize(18);
    doc.text(companyName, 15, 15); // Company name - left aligned

    // Draw horizontal border under header
    doc.setDrawColor(22, 145, 255);
    doc.setLineWidth(0.5);
    doc.line(15, 18, pageWidth - 15, 18); // Border line

    // === Title (below border, centered) ===
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(title, pageWidth / 2, 26, { align: "center" }); // Title below border

    // === Table Data ===
    const columns =
      reportType === "Agent Collection Report"
        ? agentCollectionLogColumns
        : reportType === "Collection Report"
          ? collectionColumns
          : reportType === "Fines Report"
            ? fineReportColumns
            : statePerformanceColumns;

    let startY = 32;

    if (options?.summaryRows?.length) {
      autoTable(doc, {
        head: [
          ["S.No", ...agentCollectionSummaryColumns.map((col) => col.label)],
        ],
        body: buildPdfRows(options.summaryRows, agentCollectionSummaryColumns),
        startY,
        theme: "grid",
        headStyles: {
          fillColor: [22, 145, 255],
          textColor: 255,
          fontSize: 10,
        },
        styles: { fontSize: 9 },
      });
      startY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text(options.detailTitle ?? "Entries-Logs", 15, startY);
      startY += 6;
    }

    const headers = [["S.No", ...columns.map((col) => col.label)]];

    const pdfRows = buildPdfRows(rows, columns);

    autoTable(doc, {
      head: headers,
      body: pdfRows,
      startY,
      theme: "grid",
      headStyles: {
        fillColor: [22, 145, 255],
        textColor: 255,
        fontSize: 10,
      },
      styles: {
        fontSize: 9,
      },
      didDrawPage: (data: any) => {
        const pageCount = doc.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;

        // Footer: page number (left)
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${doc.getCurrentPageInfo().pageNumber} of ${pageCount}`,
          data.settings.margin.left,
          pageHeight - 5,
        );

        // Footer: generated timestamp (right)
        doc.text(
          `Generated at: ${generatedAt}`,
          pageWidth - 15,
          pageHeight - 5,
          { align: "right" },
        );
      },
    });

    doc.save(`${title.replace(" ", "_")}.pdf`);
    dispatch(getReportData({ ...pageProps, perPage: 10 }));
  };

  const fetchFullAgentCollectionReport = () =>
    dispatch(
      getReportData({
        ...pageProps,
        reportType: "Agent Collection Report",
        perPage: data.totalRecords || pageProps.perPage,
        pageNumber: 1,
      } as any),
    );

  const handleDownloadExcel = () => {
    fetchFullAgentCollectionReport().then((res: any) => {
      if (res.payload?.responseCode === "200") {
        const result = res.payload?.result ?? {};
        downloadAgentCollectionExcel(
          result.summary ?? [],
          result.logEntries?.list ?? [],
        );
        dispatch(
          getReportData({
            ...pageProps,
            reportType: "Agent Collection Report",
            perPage: pageProps.perPage || 10,
            pageNumber: pageProps.pageNumber || 1,
          } as any),
        );
      }
    });
  };

  const handleDownloadPdf = () => {
    if (reportType === "Agent Collection Report") {
      fetchFullAgentCollectionReport().then((res: any) => {
        if (res.payload?.responseCode === "200") {
          const result = res.payload?.result ?? {};
          generatePdf(result.logEntries?.list ?? [], {
            summaryRows: result.summary ?? [],
            detailTitle: "Entries-Logs",
          });
        }
      });
      return;
    }

    dispatch(
      getReportData({ ...pageProps, perPage: data.totalRecords } as any),
    ).then((res: any) => {
      if (res.payload?.responseCode === "200") {
        generatePdf(res.payload?.result?.list ?? []);
      }
    });
  };

  const hasExportData =
    data.list.length > 0 || (data.summary?.length ?? 0) > 0;

  return (
    <Grid container spacing={2} alignItems="flex-start" className="pt-4 pb-2">
      {/* Report Type */}
      <Grid item xs={6} md={2}>
        <Autocomplete
          fullWidth
          options={reportTypes
            .filter((i) => hasReportPermission(user?.UserRole, i.permissins))
            .map((rt: any) => rt.label)}
          value={reportType}
          onChange={(e, value) => {
            dispatch(clearReportsData());
            dispatch(onChangeReportsList({ ...pageProps, reportType: value }));
            setReportType(value || "Fines Report");
            setDateErrors({});
            setFormData((prev) => ({
              ...prev,
              agent: "all",
              createdBy: "all",
              fineStatus: "all",
              offenceCode: "all",
            }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Report Type" size="small" />
          )}
        />
      </Grid>

      {/* Start Date */}
      <Grid item xs={6} sm={4} md={2} sx={{ display: "flex" }}>
        <TextField
          type="date"
          fullWidth
          label={datesOptional ? "Start Date (optional)" : "Start Date"}
          name="startDate"
          value={formData.startDate || ""}
          onChange={handleChange}
          onBlur={() =>
            setDateErrors(
              runDateValidation(
                formData.startDate || "",
                formData.endDate || "",
              ),
            )
          }
          error={Boolean(dateErrors.startDate)}
          helperText={dateErrors.startDate || " "}
          FormHelperTextProps={{ sx: dateHelperTextSx }}
          inputProps={{
            max: formData.endDate || todayDateString(),
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Grid>

      {/* End Date */}
      <Grid item xs={6} sm={4} md={2} sx={{ display: "flex" }}>
        <TextField
          type="date"
          fullWidth
          label={datesOptional ? "End Date (optional)" : "End Date"}
          name="endDate"
          value={formData.endDate || ""}
          onChange={handleChange}
          onBlur={() =>
            setDateErrors(
              runDateValidation(
                formData.startDate || "",
                formData.endDate || "",
              ),
            )
          }
          error={Boolean(dateErrors.endDate)}
          helperText={dateErrors.endDate || " "}
          FormHelperTextProps={{ sx: dateHelperTextSx }}
          inputProps={{
            min: formData.startDate || undefined,
            max: todayDateString(),
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Grid>

      {/* Fine Status (Fines Report only) */}
      {reportType === "Fines Report" && (
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            label="Status"
            name="fineStatus"
            value={formData.fineStatus}
            size="small"
            onChange={handleChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Paid">Paid</MenuItem>
          </TextField>
        </Grid>
      )}

      {/* Created By / RSCOfficer (Fines Report only) */}
      {reportType === "Fines Report" && (
        <Grid item xs={6} md={2}>
          <Autocomplete
            fullWidth
            loading={usersLoading}
            options={[
              "All",
              ...(users
                ?.filter((u: any) => u.roleName?.toLowerCase() === "warden")
                .map((u: any) => u.name) || []),
            ]}
            value={formData.createdBy === "all" ? "All" : formData.createdBy}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                createdBy: !value || value === "All" ? "all" : value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="RSCOfficer"
                size="small"
                placeholder="Select RSCOfficer"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {/* Agent (Collection Report only) */}
      {reportType === "Collection Report" && (
        <Grid item xs={6} md={2}>
          <Autocomplete
            fullWidth
            loading={agentsLoading}
            options={[
              "All",
              ...(subAgents?.map(
                (agent: any) => agent.firstName + " " + agent?.lastName,
              ) || []),
            ]}
            value={formData.agent === "all" ? "All" : formData.agent}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                agent: !value || value === "All" ? "all" : value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agent"
                size="small"
                placeholder="Select Agent"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {agentsLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {/* Offense Code (Fines & Collection reports) */}
      {(reportType === "Fines Report" ||
        reportType === "Collection Report") && (
        <Grid item xs={6} md={2}>
          <Autocomplete
            fullWidth
            loading={offencesLoading}
            options={[
              "All",
              ...(offences?.map((o: any) => o.offenceCode) || []),
            ]}
            value={
              formData.offenceCode === "all" ? "All" : formData.offenceCode
            }
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                offenceCode: !value || value === "All" ? "all" : value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Offense Code"
                size="small"
                placeholder="Select Offense"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {offencesLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {/* Submit Button */}
      <Grid
        item
        xs={12}
        md="auto"
        className="flex gap-2 items-center flex-nowrap"
        sx={{ pt: { xs: 0, md: "2px" }, flexWrap: "nowrap" }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Submit
        </Button>
        {hasExportData && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDownloadPdf}
            startIcon={<Download />}
            disabled={loading}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Download PDF
          </Button>
        )}
        {reportType === "Agent Collection Report" && hasExportData && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleDownloadExcel}
            startIcon={<TableView />}
            disabled={loading}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Download Excel
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default FinesReportForm;
